import React from 'react';
import { Row, Col, Alert, Spinner, Form, Button } from 'react-bootstrap';
import SimpleMantenedorLayout from '../../components/SimpleMantenedorLayout';
import { useEstadosTiposYFiltrado } from '../../hooks/useEstadosTiposYFiltrado';
import { useMantenedorPermissions } from '../../hooks/useMantenedorPermissions';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import SubEstadoModal from '../../components/Estados/modals/SubEstadoModal';
import EstadoModal from '../../components/Estados/modals/EstadoModal';
import * as estadosApi from '@shared/helpers/api/mantenedores/estados';
import { SuccessSwal, ErrorSwal } from '@shared/helpers/swal';
import EstadosSubestadosTable from '../../components/Estados/EstadosSubestadosTable';

const EstadosSubestados = () => {
  const { userPermissions, currentUser } = useMantenedorPermissions();
  
  const estadosPerms = userPermissions?.modules?.estados || {};
  
  const canAdmin = !!estadosPerms.canAdmin;
  const canView = !!estadosPerms.canView;

  const {
    tipos,
    selectedTipo,
    setSelectedTipo,
    estados,
    loading,
    error
  } = useEstadosTiposYFiltrado(canAdmin);


  // Modal state
  const [showSubEstadoModal, setShowSubEstadoModal] = React.useState(false);
  const [showEstadoModal, setShowEstadoModal] = React.useState(false);


  // Estado y lógica para crear estado
  const [loadingEstado, setLoadingEstado] = React.useState(false);
  const [errorEstado, setErrorEstado] = React.useState(null);


  // Refactor: función para manejar la creación de estado
  const handleCreateEstado = async (formData) => {
    setLoadingEstado(true);
    setErrorEstado(null);
    if (!formData.estado || !selectedTipo) {
      setErrorEstado('Debe ingresar el nombre del estado y seleccionar un tipo.');
      setLoadingEstado(false);
      ErrorSwal('Error', 'Debe ingresar el nombre del estado y seleccionar un tipo.');
      return;
    }
    try {
      const body = {
        estado: formData.estado,
        tipo_estado_id: selectedTipo,
        descripcion: formData.descripcion || ''
      };
      const resp = await estadosApi.createEstado(currentUser, body);
      if (resp.success) {
        setShowEstadoModal(false);
        setErrorEstado(null);
        setLoadingEstado(false);
        SuccessSwal('¡Estado creado!', resp.message || 'El estado fue creado exitosamente.', true);
        return;
      } else {
        setErrorEstado(resp.error || 'Error al crear estado');
        ErrorSwal('Error', resp.error || 'Error al crear estado');
      }
    } catch (err) {
      setErrorEstado('Error inesperado al crear estado');
      ErrorSwal('Error', 'Error inesperado al crear estado');
    }
    setLoadingEstado(false);
  };

  // Handler para crear subestado
  const handleCreateSubEstado = async (formData) => {
    // formData: { sub_estado, descripcion, estadoId }
    if (!formData.estadoId) {
      ErrorSwal('Error', 'Debe seleccionar un estado padre.');
      return { success: false };
    }
    try {
      const resp = await estadosApi.createSubEstado(currentUser, formData.estadoId, {
        sub_estado: formData.sub_estado,
        descripcion: formData.descripcion
      });
      if (resp.success) {
        setShowSubEstadoModal(false);
        SuccessSwal('¡Subestado creado!', resp.message || 'El subestado fue creado exitosamente.', true);
        return { success: true };
      } else {
        ErrorSwal('Error', resp.error || 'Error al crear subestado');
        return { success: false, errors: resp.errors };
      }
    } catch (err) {
      ErrorSwal('Error', 'Error inesperado al crear subestado');
      return { success: false };
    }
  };

  React.useEffect(() => {
    console.log('Permisos de usuario en estados:', userPermissions);
    console.log('Estados Permisos:', estadosPerms);
  }, [userPermissions, estadosPerms]);

  return (
    <SimpleMantenedorLayout
      mantenedorId="estados"
      title="Estados y Sub Estados"
      subtitle="Gestión de Estados y Sub Estados del sistema"
      showBackButton={false}
    >

      <div className="container-fluid">
        <div className="row mb-3 align-items-end">
          <div className="col-md-6 col-12">
            {canAdmin && (
              <div style={{ minWidth: 260 }}>
                <Form.Label>Tipo de Estado</Form.Label>
                <Form.Select
                  value={selectedTipo || ''}
                  onChange={e => setSelectedTipo(Number(e.target.value))}
                  style={{ maxWidth: 260 }}
                >
                  <option value="">Seleccione tipo de estado...</option>
                  {tipos.map(tipo => (
                    <option key={tipo.id} value={tipo.id}>{tipo.tipo_estado}</option>
                  ))}
                </Form.Select>
              </div>
            )}
          </div>
          <div className="col-md-6 col-12 d-flex justify-content-md-end justify-content-start mt-3 mt-md-0" style={{ gap: 12 }}>
            {canAdmin && (
              <Button variant="primary" onClick={() => setShowEstadoModal(true)}>
                <FaPlus style={{ marginRight: 6 }} /> Estado
              </Button>
            )}
            <Button variant="success" onClick={() => setShowSubEstadoModal(true)}>
              <FaPlus style={{ marginRight: 6 }} /> SubEstado
            </Button>
          </div>
        </div>

        {/* Estado de carga */}
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" role="status" />
            <p className="mt-2">Cargando datos de estados...</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <Alert variant="danger">
            <h6>❌ Error al cargar datos</h6>
            <p>{error}</p>
          </Alert>
        )}

        {/* Tabla de estados y subestados */}
        {!loading && !error && (
          <EstadosSubestadosTable estados={estados} canAdmin={canAdmin} />
        )}

        {/* Modal para crear subestado */}
        <SubEstadoModal
          show={showSubEstadoModal}
          onHide={() => setShowSubEstadoModal(false)}
          estadosForSelect={estados[0]?.estados?.map(e => ({ value: e.id, label: e.estado })) || []}
          tipoEstadoNombre={canAdmin
            ? (tipos.find(t => t.id === selectedTipo)?.tipo_estado || '')
            : 'guia'}
          loading={false}
          onSave={handleCreateSubEstado}
          canAdmin={canAdmin}
        />
        {/* Modal para crear estado (solo admin) */}
        <EstadoModal
          show={showEstadoModal}
          onHide={() => {
            setShowEstadoModal(false);
            setErrorEstado(null);
          }}
          tipoEstadoNombre={canAdmin ? (tipos.find(t => t.id === selectedTipo)?.tipo_estado || '') : ''}
          loading={loadingEstado}
          error={errorEstado}
          onSave={handleCreateEstado}
        />
      </div>
    </SimpleMantenedorLayout>
  );
};

export default EstadosSubestados;